import { useEffect, useState } from 'react'
import { Button } from '../common/Button'

export interface TruppsBerichtApi {
  get: (url: string, config?: any) => Promise<{ data: any; headers?: any }>
}

interface TruppMitglied { name: string; zeitraeume: string; tf: boolean; tf_zeitraeume: string }
interface TruppUebersicht {
  key?: string; aufgeloest?: boolean; name: string; typ: string; status: string; zeitraum: string; fuehrer: string; reserve?: string
  fuehrer_verlauf: { name: string; zeitraum: string }[]; mitglieder: TruppMitglied[]
}
interface TruppsBericht {
  vorhanden: boolean; uebersicht?: TruppUebersicht[]; protokoll?: { zeit: string; trupp: string; text: string }[]; ende_text?: string
}

const dateiName = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim()

/**
 * Abschnitt „Trupps“ des Einsatzberichts: Übersicht aller Trupps (auch aufgelöste) mit Truppführer-Verlauf und Mitgliedern
 * samt Zeiträumen, dazu aufklappbares Änderungsprotokoll (Ortszeit) und PDF-Download. Ohne Trupps wird nichts angezeigt.
 * Gleiche Komponente in Führung und Admin (`api` = axios-Instanz mit Basis /api/v1).
 */
export function EinsatzTruppsBericht({ einsatzId, einsatzName, einsatznummer, api }: {
  einsatzId: number; einsatzName?: string | null; einsatznummer?: string | null; api: TruppsBerichtApi
}) {
  const [daten, setDaten] = useState<TruppsBericht | null>(null)
  const [fehler, setFehler] = useState('')
  const [pdfLaedt, setPdfLaedt] = useState(false)
  const [pdfFehler, setPdfFehler] = useState('')

  useEffect(() => {
    let lebt = true
    setDaten(null); setFehler('')
    api.get(`/einsaetze/${einsatzId}/trupps-bericht`)
      .then(r => { if (lebt) setDaten(r.data) })
      .catch(() => { if (lebt) setFehler('Die Trupps konnten nicht geladen werden.') })
    return () => { lebt = false }
  }, [api, einsatzId])

  const pdfLaden = async () => {
    setPdfLaedt(true); setPdfFehler('')
    try {
      const r = await api.get(`/einsaetze/${einsatzId}/trupps-bericht.pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = dateiName(`Trupp-Protokoll ${[einsatznummer, einsatzName].filter(Boolean).join(' ') || einsatzId}`) + '.pdf'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    } catch {
      setPdfFehler('Das PDF konnte nicht erstellt werden.')
    } finally {
      setPdfLaedt(false)
    }
  }

  if (fehler) return <p className="border-t border-dark-border pt-4 text-xs text-red-300">{fehler}</p>
  if (!daten || !daten.vorhanden) return null
  const trupps = daten.uebersicht ?? []
  const protokoll = daten.protokoll ?? []
  const aktive = trupps.filter(t => !t.aufgeloest)
  const aufgeloeste = trupps.filter(t => t.aufgeloest)
  const truppKarte = (t: TruppUebersicht, i: number) => (<div key={t.key ?? i} className="min-w-0 rounded-xl border border-dark-border p-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="break-words text-sm font-semibold text-dark-text">{t.name}</span>
              <span className="text-xs text-dark-text-muted">{t.typ}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${t.status === 'aktiv' ? 'bg-green-500/15 text-green-300' : t.status === 'aufgelöst' ? 'bg-dark-bg-tertiary text-dark-text-subtle' : 'bg-amber-500/15 text-amber-300'}`}>{t.status}</span>
            </div>
            <div className="mt-1 text-xs text-dark-text-subtle">Zeitraum: {t.zeitraum}</div>
            {t.reserve && <div className="text-xs text-amber-300/90">Reserve Wache: {t.reserve}</div>}
            <dl className="mt-2 space-y-1 text-sm">
              <div className="sm:flex sm:gap-2">
                <dt className="shrink-0 text-xs text-dark-text-subtle sm:w-32">Truppführer</dt>
                <dd className="min-w-0 break-words text-dark-text">
                  {t.fuehrer_verlauf.length === 0 ? '–' : t.fuehrer_verlauf.map((f, k) => (
                    <div key={k}>{f.name} <span className="text-xs text-dark-text-subtle">({f.zeitraum})</span></div>
                  ))}
                </dd>
              </div>
              <div className="sm:flex sm:gap-2">
                <dt className="shrink-0 text-xs text-dark-text-subtle sm:w-32">Mitglieder</dt>
                <dd className="min-w-0 break-words text-dark-text">
                  {t.mitglieder.length === 0 ? '–' : t.mitglieder.map((m, k) => (
                    <div key={k}>
                      {m.name}{m.tf ? ' (TF)' : ''}{' '}
                      <span className="text-xs text-dark-text-subtle">({m.zeitraeume}{m.tf_zeitraeume ? `; TF: ${m.tf_zeitraeume}` : ''})</span>
                    </div>
                  ))}
                </dd>
              </div>
            </dl>
          </div>)

  return (
    <div className="min-w-0 space-y-3 border-t border-dark-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-dark-text">Trupps</h3>
        <Button variant="secondary" size="sm" onClick={pdfLaden} disabled={pdfLaedt}>
          {pdfLaedt ? 'PDF wird erstellt …' : 'Trupp-Protokoll als PDF'}
        </Button>
      </div>
      {pdfFehler && <p className="text-xs text-red-300">{pdfFehler}</p>}

      {aktive.length > 0 && (
        <div className="space-y-2">
          {aufgeloeste.length > 0 && <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-text-muted">Aktive Trupps</h4>}
          {aktive.map(truppKarte)}
        </div>
      )}
      {aufgeloeste.length > 0 && (
        <div className="space-y-2 border-t border-dark-border pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-text-muted">Aufgelöste Trupps</h4>
          {aufgeloeste.map(truppKarte)}
        </div>
      )}

      {protokoll.length > 0 && (
        <details className="rounded-xl border border-dark-border">
          <summary className="cursor-pointer px-3 py-2 text-sm text-dark-text">Änderungsprotokoll ({protokoll.length})</summary>
          <ul className="divide-y divide-dark-border border-t border-dark-border">
            {protokoll.map((p, i) => (
              <li key={i} className="px-3 py-2 text-xs sm:flex sm:gap-3">
                <span className="shrink-0 text-dark-text-subtle sm:w-36">{p.zeit}</span>
                <span className="min-w-0 break-words text-dark-text"><span className="text-dark-text-muted">{p.trupp}: </span>{p.text}</span>
              </li>
            ))}
          </ul>
          {daten.ende_text && <p className="border-t border-dark-border px-3 py-2 text-[11px] text-dark-text-subtle">Zeiten in Ortszeit. TF = Truppführer.</p>}
        </details>
      )}
    </div>
  )
}
